/**
 * Payment Validation Utilities
 * Centralized logic for validating and sanitizing payment data and API responses.
 *
 * @module paymentValidation
 */

/**
 * Generic schema-based validation.
 *
 * @private
 * @param {Object} data - Input data
 * @param {Object} schema - Validation rules per key
 * @returns {Object} { isValid: boolean, errors: string[], sanitized: Object }
 */
const validate = (data, schema) => {
  const errors = []
  const sanitized = {}

  if (!data || typeof data !== 'object') {
    return { isValid: false, errors: ['Invalid input data'], sanitized: {} }
  }

  for (const [key, rules] of Object.entries(schema)) {
    const value = data[key]
    const isEmpty = value === undefined || value === null || value === ''

    if (rules.required && isEmpty) {
      errors.push(`Campo obbligatorio mancante: ${key}`)
      continue
    }

    if (isEmpty) continue

    if (rules.type && typeof value !== rules.type) {
      errors.push(`Tipo non valido per ${key}: atteso ${rules.type}`)
      continue
    }

    if (rules.type === 'string') {
      const val = String(value).trim()
      if (rules.minLength && val.length < rules.minLength)
        errors.push(`${key} troppo corto`)
      if (rules.maxLength && val.length > rules.maxLength)
        errors.push(`${key} troppo lungo`)
      if (rules.pattern && !rules.pattern.test(val))
        errors.push(`${key} formato non valido`)
      sanitized[key] = val
    } else {
      sanitized[key] = value
    }
  }

  return { isValid: errors.length === 0, errors, sanitized }
}

/**
 * Validates query parameters for single payment confirmation.
 */
export const validateSinglePaymentParams = (params) => {
  return validate(params, {
    ckey: { required: true, type: 'string' },
    token: { required: false, type: 'string' },
    orderID: { required: false, type: 'string' },
    operationnumber: { required: false, type: 'string' },
  })
}

/**
 * Validates query parameters for recurrent payment confirmation.
 */
export const validateRecurrentPaymentParams = (params) => {
  return validate(params, {
    operationnumber: {
      required: true,
      type: 'string',
      pattern: /^[a-zA-Z0-9_\-]+$/,
    },
    email: {
      required: false,
      type: 'string',
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    },
    amount: {
      required: false,
      type: 'string',
      pattern: /^\d+(\.\d{1,2})?$/,
    },
    // Other fields allowed as-is
    firstname: { type: 'string' },
    lastname: { type: 'string' },
    taxcode: { type: 'string' },
    companyname: { type: 'string' },
    from: { type: 'string' },
    operationdate: { type: 'string' },
    paymentmethod: { type: 'string' },
  })
}

/**
 * Recursively sanitizes an object to prevent prototype pollution and remove functions.
 *
 * @param {Object} data
 * @returns {Object}
 */
export const sanitizePaymentData = (data) => {
  if (!data || typeof data !== 'object') return {}

  const sanitized = {}
  const forbidden = ['__proto__', 'constructor', 'prototype']

  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'function' || forbidden.includes(key)) continue

    if (Array.isArray(value)) {
      sanitized[key] = value.map((v) =>
        v && typeof v === 'object' ? sanitizePaymentData(v) : v
      )
    } else if (value && typeof value === 'object') {
      sanitized[key] = sanitizePaymentData(value)
    } else {
      sanitized[key] = value
    }
  }

  return sanitized
}

/**
 * Validates the structure of a payment order response.
 * Handles both success and error response shapes.
 *
 * @param {Object} response
 * @returns {boolean}
 */
export const validateOrderResponse = (response) => {
  if (!response || typeof response !== 'object') return false

  // Error response is structurally valid as a response
  if (response.success === false) return true

  // Success must have an order identifier
  if (response.success === true)
    return !!(response.orderID || response.id_order)

  // Fallback check for raw response data
  return !!(response.id_order || response.orderID || response.ckey)
}
